import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: forbidden. Every colour on screen is the cover's own,
// put through a broken codec.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'mosh',
  family: 'shader',
  danceability: 0.70,
  requiresTier: 1,
  energyLevel: 0.88,
  bpmMin: 115,
  bpmMax: 185,
  movement: 0.85,
  tonality: 0.45,
  prefers: { gridStrength: [0.60, 1.0], percussiveness: [0.45, 1.0] },
  enabled: true
};

// Mosh: datamosh. The cover breaks into macroblocks that travel along motion
// vectors belonging to a different picture, then snaps back whole on the
// downbeat.
//
// THE ACCIDENT IT IMITATES. Strip the keyframes out of a compressed video and
// the motion vectors of one scene keep being applied to the pixels of the
// next: the picture dissolves into trails of blocks carrying the trajectory of
// whatever came before. So the vectors here are NOT random - each field is a
// coherent camera move (pan, zoom, rotate, parallax) applied to the wrong
// picture, and that mismatch is the entire effect. Random per-block vectors
// give noise; a coherent field on the wrong frame gives datamosh.
//
// EXCEPTION TO THE NO-RENDER-TARGET CONTRACT, as granted in the brief: a
// ping-pong pair through the shared FeedbackBuffer, plus one small texture
// holding the vectors.

// --- the macroblock grid -----------------------------------------------------
//
// In SCREEN pixels. progress walks the size down through the track: coarse at
// the ends, fine at the centre, so the picture gains detail at its heart.
const BLOCK_START = 24;
const BLOCK_MID = 12;
const CHROMA_SCALE = 2.0;        // chroma blocks twice the size, as in any codec
const CHROMA_JIT_TEXELS = 9.0;   // how far the colour comes adrift from the shapes
// QUANTISED TO A WHOLE BUFFER TEXEL, which at tier 2 is two screen pixels and
// at tier 1 four. The brief asks for integer pixels and a codec's half-pel
// interpolation in the same breath; measured, half-pel is what ruins it here.
// A codec interpolates ONCE against a fresh reference, this loop interpolates
// against its own output 26 times a beat, and compounding bilinear taps is a
// low-pass - the first build came out as smooth motion blur, not as blocks
// being copied. On the texel grid the sample lands dead on a texel centre and
// the picture stays crisp however long it travels, which is exactly the
// datamosh look: sharp blocks in the wrong place.
// The vector texture stores the displacement in BUFFER TEXELS, not screen
// pixels, and the shader rounds it after decoding. A byte cannot hold a texel
// count exactly - the round trip is off by up to a fifth of a pixel - and on
// the TV, where mediump is real fp16, decoding costs another quarter of a
// texel. Either is enough to land the sample on the wrong side of a texel
// boundary and put the bilinear blur back. Rounding in the shader recovers the
// integer whatever the two errors did, because together they stay well under a
// half.
const MV_RANGE = 32.0;           // buffer texels, the encoding's half-range
// The vector texture's ceiling. The brief's 80 x 45 is exactly 24 px blocks at
// 1080p; the 12 px end of the progress ramp needs four times that, and the
// measured cost of filling and uploading it is on _writeVectors.
const MV_MAX_W = 160;
const MV_MAX_H = 90;

// --- the motion fields -------------------------------------------------------
const FIELDS = ['pan', 'zoom', 'rotate', 'parallax'];
// Amplitude in SCREEN pixels per BEAT, divided across the beat's P-frames. The
// brief gives 2 to 14 px per frame, which would be 52 to 364 px per beat at 140
// bpm and 60 fps; swept against the picture, 364 walks the cover most of the
// way across the screen inside one bar and nothing is left to recognise by the
// second beat. 230 at full intensity dissolves it thoroughly over four beats
// and still reads as the same picture displaced.
const MV_MIN_BEAT = 40.0;
const MV_MAX_BEAT = 230.0;
// P-FRAMES PER BEAT. A datamosh is a sequence of predicted frames at the video
// rate, not a continuous flow: re-applying one field sixty times a second turns
// the displacement into a directional comb, which is what the first builds
// looked like - long parallel streaks rather than blocks of picture jumping.
// Advecting on a musical subdivision instead gives the lurch the brief calls
// "le saccade caracteristique", gets a visible block-sized jump out of each
// step, and incidentally takes the advection pass from 60 to about 9 runs a
// second. It also removes every frame-rate dependence from the loop: decay,
// flattening and amplitude are all per P-frame now, and a P-frame is a fixed
// fraction of a beat whatever the display is doing.
const P_PER_BEAT = 8;            // thirty-second notes, ~19 Hz at 140 bpm
// ESTIMATION NOISE, and the single thing that makes the blocks visible at all.
// A coherent field alone gives neighbouring blocks vectors that differ by a
// fraction of a pixel, so the advection has no discontinuity to show and the
// result reads as a smooth motion blur - which is what the first build looked
// like. A real codec does not compute a field, it BLOCK-MATCHES, and its
// estimates disagree with their neighbours; that disagreement is the blocking.
// Expressed in block widths of divergence per beat, and it grows with the
// dissolve: just after a keyframe the prediction is good, and it rots.
const JITTER_BLOCKS_PER_BEAT = 0.9;


// --- the I-frame cycle -------------------------------------------------------
//
// THIS CYCLE IS THE PIECE. A keyframe on every downbeat and nothing but vectors
// for the four beats after it: sharp, progressively dissolved, sharp again -
// locked to the bar without anything having to be synchronised by hand.
const DROP_BARS_NO_KEY = 2;      // a drop skips two whole bars of keyframes
const DISSOLVE_MAX = 2.0;        // reached only across a drop's eight beats
const FROZEN_MIN = 0.05;         // share of blocks with a null vector, after a key
const FROZEN_MAX = 0.35;         // and just before the next one

// --- injected shapes ---------------------------------------------------------
//
// Without them the frame drifts to a uniform grey by the end of a cycle: the
// advection has nothing new to carry, and decay plus posterisation flatten what
// is left. One hard opaque shape per non-key beat is caught by the next frame's
// vectors and smeared into blocks straight away.
const SHAPE_SLOTS = 4;
const SHAPE_SAT_MIN = 0.85;

// --- stability ---------------------------------------------------------------
// Decay is expressed PER BEAT, not per frame. Echo's per-frame constant makes
// the survival of a trail depend on the frame rate and on the tempo: at 140 bpm
// a beat is 26 frames and 0.985 leaves 67% of the picture, at 115 bpm it is 31
// frames and leaves 62%, and on a Pi running at 50 fps the numbers change
// again. Here the cycle IS the structure, so what has to be constant is how
// much of the picture survives a BEAT - measured, at the first settings, as a
// fall from 80 to 13 mean luminance over three beats, i.e. the image dissolving
// by going black rather than by smearing.
const RETAIN_DEFAULT = 0.88;     // fraction of the picture surviving one beat
const RETAIN_MIN = 0.55;
const RETAIN_MAX = 0.94;         // never >= 1, whatever the governor does
const BLACK_BIAS_PER_BEAT = 0.010;   // weak pixels die instead of pooling to grey
// How hard a block is pulled towards its own flat value over one beat, the
// pull being multiplied by the dissolve so a fresh keyframe is untouched.
// Swept by measuring the luminance spread INSIDE each block against the
// picture's own spread, across a bar: at 0 that ratio GROWS 0.14 -> 0.28, the
// blocks getting more textured as the cycle runs, which is the opposite of what
// an over-compressed frame does. At 4.0 it falls 0.15 -> 0.09 and the within-
// block spread goes 12.5 -> 3.8 while the picture's own contrast holds at 42 -
// flat plates, not a slide into grey.
const FLATTEN_PER_BEAT = 4.0;
const CEILING = 0.92;
const BEAT_FALLBACK = 60 / 140;  // seconds, when no bpm is known
// Governor, same shape as Echo's. Each keyframe is already a full reset, so
// divergence is unlikely - these guard the drops, where two bars pass without
// one and the loop runs unchecked for eight beats.
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.38;
const GOVERN_RESUME = 0.28;
const GOVERN_LOW = 0.06;
const GOVERN_STEP = 0.010;
const LUM_SIZE = 8;

const SAFE_FLOOR = 0.50;

const TIER = {
  // blockScale multiplies the progress ramp: tier 1 runs 48 -> 24 px blocks,
  // tier 2 runs 24 -> 12.
  1: { scale: 0.25, blockScale: 2.0, chroma: false, frozen: true },
  2: { scale: 0.5, blockScale: 1.0, chroma: true, frozen: true }
};
const DEFAULT_TIER = 2;

const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADERS
// =============================================================================

// Both passes draw the same unit plane straight into clip space - no camera is
// involved, so the quad cannot be mis-framed by one.
const QUAD_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function moshFragment(layout) {
  // CHROMINANCE DRIFTS ON ITS OWN, in blocks twice the size. Every codec
  // subsamples chroma, so when the prediction breaks the colour comes adrift
  // from the shapes and smears in a coarser grid than the luma does. It is the
  // artefact that most says "compressed video" rather than "warped picture".
  const chroma = layout.chroma ? `
    vec2 cCount = uBlockCount / ${CHROMA_SCALE.toFixed(1)};
    vec2 cCell = (floor((uv + uGridOffset) * cCount) + 0.5) / cCount;
    vec4 cmv = texture2D(uMotion, cCell);
    // Its OWN offset per chroma block, not just a multiple of the luma vector.
    // Scaling alone leaves two neighbouring chroma cells differing by nothing
    // but the estimation noise, so there is no discontinuity at their shared
    // border and no chroma block structure to see - measured as a jump ratio of
    // 0.99x between chroma-block boundaries and luma-only ones, where a real
    // subsampled codec shows the colour breaking on the coarser grid.
    vec2 cb = floor((uv + uGridOffset) * cCount);
    vec2 cJit = (vec2(hash12(cb), hash12(cb + 19.7)) - 0.5) * uChromaJit;
    vec2 cVec = floor((cmv.xy * 2.0 - 1.0) * ${MV_RANGE.toFixed(1)} + cJit + 0.5);
    vec3 cPrev = texture2D(uPrevious, fract(uv - cVec * uInvBuf)).rgb;
    float lum  = dot(prev,  vec3(0.299, 0.587, 0.114));
    float cLum = dot(cPrev, vec3(0.299, 0.587, 0.114));
    // Luma from THIS block, chrominance from the coarser displaced one. LAST,
    // after the flattening: the flattening resamples at the LUMA block centre
    // at up to 0.9 weight, so run before it the chroma offset is pulled straight
    // back onto the luma grid - measured as a chroma discontinuity ratio of
    // 1.00x between chroma-block boundaries and luma-only ones, i.e. no chroma
    // block structure at all.
    prev = mix(prev, clamp(vec3(lum) + (cPrev - vec3(cLum)), 0.0, 1.0), d1 * 0.85);
  ` : '';

  return `
  precision mediump float;
  uniform sampler2D uPrevious;
  uniform sampler2D uMotion;
  uniform sampler2D uArtwork;
  uniform float uHasArtwork;
  uniform vec2 uBlockCount;
  uniform vec2 uInvBuf;          // one buffer texel, in uv
  uniform vec2 uGridOffset;
  uniform float uKeyMix;
  uniform float uDissolve;       // 0..1 across a normal bar, up to 2 on a drop
  uniform float uDecay;
  uniform float uBias;
  uniform float uFlatten;
  uniform float uChromaJit;      // buffer texels of chroma-only displacement
  uniform float uAspect;
  uniform float uArtAspect;
  uniform float uTime;
  uniform float uDebugVectors;
  uniform vec4 uShape[${SHAPE_SLOTS}];       // x, y, halfW, halfH (height units)
  uniform vec4 uShapeCol[${SHAPE_SLOTS}];    // rgb, kind (0 bar, 1 rect, 2 cross)
  varying vec2 vUv;

  float hash12(vec2 p) {
    // Inputs bounded before the multiply: mediump is real fp16 on the TV, and
    // an unbounded fract chain overflows to NaN there and only there.
    vec3 q = fract(vec3(mod(p.x, 512.0), mod(p.y, 512.0), mod(p.x, 512.0)) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  void main() {
    vec2 uv = vUv;
    float d1 = min(uDissolve, 1.0);

    // WHICH MACROBLOCK, and its one vector. The displacement is constant across
    // the whole block - that spatial quantisation is what makes this read as a
    // codec rather than as a warp, so nothing here may interpolate between
    // neighbouring blocks. The vector texture is sampled NEAREST at cell
    // centres for exactly that reason.
    vec2 cell = (floor((uv + uGridOffset) * uBlockCount) + 0.5) / uBlockCount;
    vec4 mv = texture2D(uMotion, cell);
    vec2 vecTx = floor((mv.xy * 2.0 - 1.0) * ${MV_RANGE.toFixed(1)} + 0.5);

    // FROZEN BLOCKS: islands of the previous picture that simply stay put while
    // everything around them travels. The most recognisable artefact of the
    // lot, and their share grows through the cycle.
    float frozen = step(0.5, mv.z);
    vecTx *= (1.0 - frozen);

    if (uDebugVectors > 0.5) {
      gl_FragColor = vec4(mv.xy, frozen, 1.0);
      return;
    }

    vec2 src = fract(uv - vecTx * uInvBuf);
    vec3 prev = texture2D(uPrevious, src).rgb;

    prev *= uDecay;
    prev = max(prev - vec3(uBias), vec3(0.0));
    prev = min(prev, vec3(${CEILING.toFixed(2)}));

    // DCT QUANTISATION. A codec throws away high frequencies first, so as the
    // prediction decays each block flattens into a handful of levels. That is
    // what turns the blocks into the flat plates an over-compressed frame
    // shows, and also what keeps the palette frank at the end of a cycle.
    // The other half of it: the fine detail INSIDE a block is thrown away
    // first, so the block converges on one flat value. Pulling each pixel
    // towards its own block's centre sample does that, and because it compounds
    // frame by frame the block is a plate well before the next keyframe. Without
    // it the frame measured as an ever FINER mosaic through the cycle (flat 3x3
    // neighbourhoods falling 0.44 -> 0.02) - the opposite of what an
    // over-compressed picture does.
    vec2 srcCentre = fract(cell - vecTx * uInvBuf);
    // 'plate', not 'flat': flat is a reserved interpolation qualifier in GLSL
    // and the program fails to compile with it as a variable name.
    vec3 plate = texture2D(uPrevious, srcCentre).rgb;
    prev = mix(prev, plate, clamp(uFlatten * d1, 0.0, 0.9));

    float levels = mix(64.0, 4.0, d1) - max(0.0, uDissolve - 1.0) * 1.5;
    levels = max(levels, 2.5);
    prev = floor(prev * levels + 0.5) / levels;
${chroma}

    // THE KEYFRAME. The cover, full frame, full opacity, nothing blended: the
    // whole point of an I-frame is that it owes nothing to what came before.
    vec3 key;
    if (uHasArtwork > 0.5) {
      // Cover-fit: scale by the larger factor and crop, never letterbox.
      vec2 a = uv - 0.5;
      float fa = uAspect / max(uArtAspect, 0.001);
      if (fa > 1.0) a.y /= fa; else a.x *= fa;
      key = texture2D(uArtwork, clamp(a + 0.5, 0.001, 0.999)).rgb;
    } else {
      vec2 g = floor(uv * 8.0);
      key = vec3(hash12(g), hash12(g + 3.1), hash12(g + 7.7)) * 0.75 + 0.12;
    }
    vec3 col = mix(prev, key, uKeyMix);

    // THE INJECTED SHAPES, last: they are new picture content, and the next
    // frame's vectors drag them into blocks immediately.
    for (int i = 0; i < ${SHAPE_SLOTS}; i++) {
      vec4 s = uShape[i];
      if (s.z <= 0.0) continue;
      vec2 dd = abs(uv - s.xy);
      dd.x *= uAspect;                      // shapes sized in height units
      float hit = step(dd.x, s.z) * step(dd.y, s.w);
      if (uShapeCol[i].a > 1.5) {
        hit = max(step(dd.x, s.z) * step(dd.y, s.w * 0.22),
                  step(dd.x, s.z * 0.22) * step(dd.y, s.w));
      }
      col = mix(col, uShapeCol[i].rgb, hit);
    }

    // Dither, or the flat plates band against each other on an 8-bit panel.
    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) * (1.4 / 255.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// Display pass: the buffer, plus the safe zone. Evaluated per fragment rather
// than per vertex - the quad has four corners and the attenuation ramp is a
// non-linear SDF, so interpolating it across two triangles would be wrong.
const SHOW_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform vec2 uBlockCount;
  uniform vec2 uGridOffset;
  uniform vec2 uBlockScreenPx;
  uniform float uDissolve;
  varying vec2 vUv;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;

    // BLOCKING, one screen pixel of darkening on each macroblock border, in
    // proportion to how long it has been since the last keyframe.
    //
    // DISPLAYED, not stored. Inside the feedback loop this multiplies the same
    // border pixels every frame and compounds: at 12 px blocks on a half-res
    // buffer a block is six texels across, a one-texel border is a third of
    // them, and 40% removed 26 times a beat takes them to zero - measured as
    // mean luminance 80 -> 43 -> 24 across a bar with nothing else changed.
    // A codec's blocking is a discontinuity in the prediction anyway, not
    // energy leaving the picture, and it belongs to the CURRENT grid rather
    // than being carried along by the vectors.
    // One SCREEN pixel wide, measured in screen pixels rather than in buffer
    // texels: at half resolution a half-texel band came out a third of a pixel
    // and the darkening measured 7.6% at the end of a bar - present in the
    // arithmetic, invisible on the panel, and what little there was got
    // smeared by the bloom.
    vec2 inBlock = fract((vUv + uGridOffset) * uBlockCount);
    vec2 edgePx = min(inBlock, 1.0 - inBlock) * uBlockScreenPx;
    // A LINE, not a ramp: smoothed over its own width it averaged 12% of the
    // intended darkening and the bloom took most of what was left.
    float onEdge = 1.0 - smoothstep(1.0, 1.6, min(edgePx.x, edgePx.y));
    col *= 1.0 - onEdge * min(uDissolve, 1.0) * 0.55;

    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    // Own floor of ${SAFE_FLOOR.toFixed(2)}, per the brief: the cover is meant to be seen
    // dissolving behind its own sharp DOM copy, which needs a shallower ramp
    // than the shared default.
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }
`;

// =============================================================================

export class MoshVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 1;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._lastBeatPhase = 0;
    this._lastBarPhase = 0;
    this._lastPhraseIndex = null;
    this._beatsSinceKey = 0;
    this._barsToSkip = 0;
    this._lastDropAt = -1e9;
    this._keyThisFrame = 0;
    this._barTookThisBeat = false;
    this._dissolve = 0;
    this._retain = RETAIN_DEFAULT;
    this._beatDur = BEAT_FALLBACK;
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._field = 0;
    this._frozenSalt = 1;
    this._lowBand = 0.5;
    this._highBand = 0.5;
    this._blockPx = BLOCK_START;
    this._pAccum = 0;
    this._advectNow = true;
    const pb = parseInt(urlParam('moshp'), 10);
    this._pPerBeat = Number.isFinite(pb) ? Math.max(1, Math.min(24, pb)) : P_PER_BEAT;
    const jg = parseFloat(urlParam('moshjitter'));
    this._jitterGain = Number.isFinite(jg) ? jg : 1;
    this._lastBlockPx = -1;
    this._lastArtworkUrl = null;
    this._lastPalette = null;
    this._artworkTexture = null;
    this._vivid = [0xff2d55, 0x00e5ff, 0xffe600, 0x7cff3d];
    this._writeVectorsPending = true;
    this._gridMargin = 0;
    this._gridMarginPx = 0;

    const fm = urlParam('moshfield');
    this._forcedField = fm && FIELDS.indexOf(fm) >= 0 ? FIELDS.indexOf(fm) : null;
    this._forceKey = urlParam('moshkey') === '1';
    this._debugVectors = urlParam('moshvectors') === '1';
    const bp = parseInt(urlParam('moshblock'), 10);
    this._forcedBlock = Number.isFinite(bp) ? Math.max(4, Math.min(96, bp)) : null;
  }

  init(scene) {
    this._scene = scene;
    const self = this;
    const L = this._layout;

    // WRAPPING, not clamping. With a pan of 8 px per frame at 60 fps the
    // vectors sweep a third of the width off the edge every beat, and a clamped
    // buffer answers by smearing the last column across everything that
    // arrives - measured as mean luminance falling 80 -> 24 over three beats,
    // i.e. the picture evacuating the frame instead of dissolving in it. Wrapped,
    // what leaves one side comes back on the other and the frame stays full,
    // which is also what a real mosh looks like: the scene slides through
    // itself.
    // NEAREST, deliberately. With the vectors on the texel grid a linear tap
    // would still be exact, but only up to the rounding in fract() - one unlucky
    // 0.4999 reintroduces a neighbour's weight and the loop low-passes itself
    // from there. It also makes the half-resolution buffer read as chunky
    // pixels on the way to the screen, which is the right kind of wrong here.
    this._buf = new FeedbackBuffer({
      scale: L.scale, wrap: THREE.RepeatWrapping, filter: THREE.NearestFilter
    });

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._passScene = new THREE.Scene();
    this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null);
    this._passQuad.frustumCulled = false;
    this._passScene.add(this._passQuad);

    // The vector texture, generated on the CPU rather than by a GPU pass. pan,
    // zoom, rotate and parallax are closed forms; the grid is at most 160 x 90;
    // and it is rebuilt on BEATS, not on frames. Setting up a render pass and a
    // second target for a few thousand texels a couple of times a second would
    // cost more than the arithmetic it replaces.
    this._mvW = 1;
    this._mvH = 1;
    this._mvBuffer = new ArrayBuffer(MV_MAX_W * MV_MAX_H * 4);
    this._mvData = new Uint8Array(this._mvBuffer);
    this._mvTex = new THREE.DataTexture(
      new Uint8Array(this._mvBuffer, 0, 4), 1, 1, THREE.RGBAFormat);
    this._mvTex.magFilter = THREE.NearestFilter;
    this._mvTex.minFilter = THREE.NearestFilter;
    this._mvTex.generateMipmaps = false;
    this._mvTex.wrapS = THREE.ClampToEdgeWrapping;
    this._mvTex.wrapT = THREE.ClampToEdgeWrapping;
    this._mvTex.needsUpdate = true;

    // A bound white texel, so the artwork sampler is never null before a cover
    // has loaded.
    this._blankTex = new THREE.DataTexture(
      new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat);
    this._blankTex.needsUpdate = true;

    const shapes = [];
    const shapeCols = [];
    for (let i = 0; i < SHAPE_SLOTS; i++) {
      shapes.push(new THREE.Vector4(0, 0, 0, 0));
      shapeCols.push(new THREE.Vector4(1, 1, 1, 0));
    }
    this._moshUniforms = {
      uPrevious: { value: null },
      uMotion: { value: this._mvTex },
      uArtwork: { value: this._blankTex },
      uHasArtwork: { value: 0 },
      uBlockCount: { value: new THREE.Vector2(40, 24) },
      uInvBuf: { value: new THREE.Vector2(1 / 640, 1 / 360) },
      uGridOffset: { value: new THREE.Vector2(0, 0) },
      uKeyMix: { value: 1 },
      uDissolve: { value: 0 },
      uDecay: { value: 1 },
      uBias: { value: 0 },
      uFlatten: { value: 0 },
      uChromaJit: { value: 0 },
      uAspect: safeZoneUniforms.uAspect,
      uArtAspect: { value: 1 },
      uTime: { value: 0 },
      uDebugVectors: { value: this._debugVectors ? 1 : 0 },
      uShape: { value: shapes },
      uShapeCol: { value: shapeCols }
    };
    this._moshMat = new THREE.ShaderMaterial({
      uniforms: this._moshUniforms,
      vertexShader: QUAD_VERTEX,
      fragmentShader: moshFragment(L),
      depthTest: false,
      depthWrite: false
    });

    this._showMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({
        uTex: { value: null },
        uBlockCount: this._moshUniforms.uBlockCount,
        uGridOffset: this._moshUniforms.uGridOffset,
        uBlockScreenPx: { value: new THREE.Vector2(24, 24) },
        uDissolve: this._moshUniforms.uDissolve
      }, safeZoneUniforms),
      vertexShader: QUAD_VERTEX,
      fragmentShader: SHOW_FRAGMENT,
      depthTest: false,
      depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._showMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -2000;
    this._quad.onBeforeRender = function (renderer) { self._run(renderer); };
    scene.add(this._quad);

    // Governor readback target, 8x8 = 256 bytes. The cost that matters is the
    // pipeline flush it forces, which is why it runs on a cadence.
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      depthBuffer: false,
      stencilBuffer: false
    });
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumScene = new THREE.Scene();
    this._lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._lumUniforms,
      vertexShader: QUAD_VERTEX,
      fragmentShader: LUM_FRAGMENT,
      depthTest: false,
      depthWrite: false
    }));
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);

    this._pendingShapes = [];
    this._newField();
  }

  // --- the block grid --------------------------------------------------------

  // A grid whose boundaries never land on the DOM cover's edge. The cover sits
  // directly over its own dissolving copy, so a block border running down the
  // artwork's border reads as the overlay being misaligned rather than as
  // compression - it is the one place the effect can look like a bug.
  //
  // Boundaries sit where (uv + off) * n is an integer. For each cover edge e we
  // want frac((e + off) * n) as far from 0 as possible, i.e. the edge sitting
  // mid-block.
  //
  // The COUNT is searched as well as the offset. With two edges per axis, what
  // an offset alone can achieve is capped by the gap between them: if that gap
  // is half a block, the best any offset does is a quarter of a block, and on
  // the Pi's own framing that came to 1.9 px. Moving the count by a block or
  // two changes the gap itself and buys back the margin, at a block size within
  // a few percent of the one progress asked for.
  _gridPlan(nx0, ny0) {
    // R19: the block grid is laid out from the centre of composition, not
    // from wherever the cover is.
    const c = safeZoneUniforms.uStageCenter.value;
    const h = safeZoneUniforms.uStageHalfSize.value;
    const a = safeZoneUniforms.uAspect.value || 1.78;
    // Safe-zone units are "height units" (frame height 2) with x already scaled
    // by aspect - convert both axes back to UV.
    const ex = [(c.x - h.x) / (2 * a) + 0.5, (c.x + h.x) / (2 * a) + 0.5];
    const ey = [(c.y - h.y) / 2 + 0.5, (c.y + h.y) / 2 + 0.5];

    function bestOffset(edges, n) {
      let best = 0;
      let bestScore = -1;
      for (let k = 0; k < 32; k++) {
        const off = k / 32;
        let score = 1;
        for (let i = 0; i < edges.length; i++) {
          let f = ((edges[i] + off) * n) % 1;
          if (f < 0) f += 1;
          score = Math.min(score, Math.min(f, 1 - f));
        }
        if (score > bestScore) { bestScore = score; best = off; }
      }
      return [best, bestScore];
    }
    function bestCount(edges, n0, lo, hi) {
      let best = null;
      // Never more than a tenth off the size progress asked for: on a coarse
      // grid a free search will happily take a 29% bigger block for a better
      // margin, and the block size is something the eye reads directly.
      const span = Math.max(2, Math.round(n0 * 0.10 / 2) * 2);
      for (let n = n0 - span; n <= n0 + span; n += 2) {
        if (n < lo || n > hi) continue;
        const r = bestOffset(edges, n);
        // Margin in block fractions, converted to a share of the ORIGINAL
        // block so a coarser grid does not win just by being coarser.
        const score = r[1] * (n0 / n);
        if (!best || score > best.score) best = { n: n, off: r[0], m: r[1], score: score };
      }
      return best;
    }
    const bx = bestCount(ex, nx0, 4, MV_MAX_W);
    const by = bestCount(ey, ny0, 4, MV_MAX_H);
    this._gridMargin = Math.min(bx.m, by.m);     // in block fractions
    return { nx: bx.n, ny: by.n, ox: bx.off / bx.n, oy: by.off / by.n };
  }

  // --- the motion field ------------------------------------------------------

  _newField() {
    this._field = this._forcedField != null
      ? this._forcedField
      : (Math.random() * FIELDS.length) | 0;
    this._fieldSeed = {
      angle: Math.random() * Math.PI * 2,
      cx: 0.25 + Math.random() * 0.5,
      cy: 0.25 + Math.random() * 0.5,
      spin: Math.random() < 0.5 ? -1 : 1,
      bands: [
        0.35 + Math.random() * 0.65,
        -(0.40 + Math.random() * 0.60),
        0.85 + Math.random() * 0.65
      ]
    };
  }

  // Rebuild the vector texture. The field is COHERENT - it describes one camera
  // move - and it is the fact that it belongs to a different picture that does
  // the work.
  _writeVectors() {
    const w = this._mvW;
    const h = this._mvH;
    const d = this._mvData;
    // Past a normal cycle - only a drop gets there - the field opens up as
    // well as lasting longer. Without this a skipped bar measured 0.29 against
    // an ordinary bar's 0.34 on the same scale: longer, but not further.
    const deep = 1 + 0.9 * Math.max(0, this._dissolve - 1);
    const perBeat = (MV_MIN_BEAT + (MV_MAX_BEAT - MV_MIN_BEAT) * clamp01(this._lowBand)) * deep;
    const pp = this._pPerBeat;
    const amp = perBeat / pp;
    const frozenFrac = this._layout.frozen
      ? FROZEN_MIN + (FROZEN_MAX - FROZEN_MIN)
        * clamp01(this._beatsSinceKey / 3.4) * (0.55 + 0.9 * clamp01(this._highBand))
      : 0;
    const sc = this._layout.scale;   // screen px -> buffer texels
    const enc = 255 / (2 * MV_RANGE);
    const s = this._fieldSeed;
    const kind = FIELDS[this._field];
    const ca = Math.cos(s.angle);
    const sa = Math.sin(s.angle);
    const salt = this._frozenSalt;
    const thresh = Math.round(frozenFrac * 1000);
    // Divergence in screen px per P-frame, from block widths per beat. The salt
    // changes on every P-frame, so this is a re-estimation rather than a fixed
    // shear - a constant per-block offset repeated all beat combs the picture.
    const jitter = this._jitterGain * JITTER_BLOCKS_PER_BEAT * this._blockPx
      * clamp01(0.15 + this._dissolve) / pp;

    for (let y = 0; y < h; y++) {
      const v = (y + 0.5) / h;
      const band = v < 1 / 3 ? 0 : (v < 2 / 3 ? 1 : 2);
      for (let x = 0; x < w; x++) {
        const u = (x + 0.5) / w;
        let mx = 0;
        let my = 0;
        if (kind === 'pan') {
          mx = ca * amp;
          my = sa * amp;
        } else if (kind === 'zoom') {
          const dx = u - s.cx;
          const dy = v - s.cy;
          const r = Math.sqrt(dx * dx + dy * dy) + 1e-4;
          // Divergence from a point, stronger further out: a dolly-in.
          mx = (dx / r) * amp * (0.35 + 1.3 * r);
          my = (dy / r) * amp * (0.35 + 1.3 * r);
        } else if (kind === 'rotate') {
          const dx = u - s.cx;
          const dy = v - s.cy;
          mx = -dy * amp * 2.4 * s.spin;
          my = dx * amp * 2.4 * s.spin;
        } else {
          // Three horizontal bands at different speeds - scenery scrolling past.
          mx = s.bands[band] * amp;
          my = 0;
        }
        // The quantisation, onto the buffer's own texel grid. The stepping it
        // leaves is the characteristic judder; a continuous field would advect
        // smoothly and read as a warp.
        let tx = Math.round(mx * sc);
        let ty = Math.round(my * sc);
        if (tx > MV_RANGE) tx = MV_RANGE; else if (tx < -MV_RANGE) tx = -MV_RANGE;
        if (ty > MV_RANGE) ty = MV_RANGE; else if (ty < -MV_RANGE) ty = -MV_RANGE;

        // Two more decorrelated draws from the same hash, for the noise.
        const hx = (((x * 374761393) ^ (y * 668265263) ^ salt) >>> 0) % 2048 / 2048 - 0.5;
        const hy = (((x * 2654435761) ^ (y * 40503) ^ (salt * 3)) >>> 0) % 2048 / 2048 - 0.5;
        mx += hx * 2 * jitter;
        my += hy * 2 * jitter;

        const hashed = (((x * 73856093) ^ (y * 19349663) ^ salt) >>> 0) % 1000;
        const i = (y * w + x) * 4;
        d[i] = Math.round(tx * enc + 127.5);
        d[i + 1] = Math.round(ty * enc + 127.5);
        d[i + 2] = hashed < thresh ? 255 : 0;
        d[i + 3] = 255;
      }
    }
    this._mvTex.needsUpdate = true;
  }

  _injectShape() {
    if (this._pendingShapes.length >= SHAPE_SLOTS) return;
    const kind = (Math.random() * 3) | 0;
    let w;
    let h;
    if (kind === 0) {                                    // bar
      const long = 0.30 + Math.random() * 0.55;
      const thin = 0.008 + Math.random() * 0.026;
      if (Math.random() < 0.5) { w = long; h = thin; } else { w = thin; h = long; }
    } else if (kind === 1) {                             // rectangle
      w = 0.05 + Math.random() * 0.16;
      h = 0.05 + Math.random() * 0.16;
    } else {                                             // cross
      w = 0.10 + Math.random() * 0.14;
      h = w;
    }
    this._pendingShapes.push({
      x: 0.06 + Math.random() * 0.88,
      y: 0.06 + Math.random() * 0.88,
      w: w,
      h: h,
      kind: kind
    });
  }

  // --- the frame -------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    this._paused = profile.playbackState === 'PAUSED';
    this._keyThisFrame = 0;
    // pause freezes on the current frame: no advection, no injection, no decay.
    if (this._paused) return;
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    this._lowBand = clamp01(bands.low);
    this._highBand = clamp01(bands.high);

    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      if (palette.vividColors && palette.vividColors.length) {
        this._vivid = palette.vividColors;
      }
    }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
      else this._moshUniforms.uHasArtwork.value = 0;
    }

    // progress: coarse blocks at the ends of the track, fine at its centre.
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 140;
    this._beatDur = Math.max(0.20, Math.min(1.2, 60 / bpm));

    const arc = Math.sin(clamp01(profile.progress || 0) * Math.PI);
    this._blockPx = this._forcedBlock != null
      ? this._forcedBlock
      : (BLOCK_START + (BLOCK_MID - BLOCK_START) * arc) * this._layout.blockScale;

    const beatPhase = profile.beatPhase || 0;
    const barPhase = profile.barPhase || 0;
    const hasGrid = director.hasReliableGrid !== false && profile.beatPhase != null;

    this._barTookThisBeat = false;
    if (hasGrid) {
      if (barPhase < this._lastBarPhase) this._onBar();
      if (beatPhase < this._lastBeatPhase) this._onBeat();
      this._lastBarPhase = barPhase;
      this._lastBeatPhase = beatPhase;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhraseIndex != null && phrase !== this._lastPhraseIndex) {
        this._newField();
      }
      this._lastPhraseIndex = phrase;
    } else {
      // No grid: a keyframe on a strong low-band rise, a beat on a floor, so
      // the cycle never stalls into a permanently dissolved frame.
      const rise = this._lowBand - (this._lastLow || 0);
      if (rise > 0.14 && this._time - (this._lastKeyAt || 0) > 1.2) {
        this._lastKeyAt = this._time;
        this._lastBeatAt = this._time;
        this._onBar();
      } else if (this._time - (this._lastBeatAt || 0) > 0.42) {
        this._lastBeatAt = this._time;
        this._onBeat();
      }
      this._lastLow = this._lowBand;
    }

    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropAt > 2.0) {
      this._lastDropAt = this._time;
      // TWO WHOLE BARS WITHOUT A KEYFRAME. The dissolve runs far past its usual
      // depth, to the point of illegibility, and the return is the harder for
      // having been withheld.
      this._barsToSkip = DROP_BARS_NO_KEY;
    }
    if (this._forceKey) { this._forceKey = false; this._onBar(); }

    // The P-frame clock. A keyframe always gets one, since it is a full
    // replacement rather than a prediction.
    this._pAccum += dt;
    const pStep = this._beatDur / this._pPerBeat;
    if (this._keyThisFrame || this._pAccum >= pStep) {
      this._pAccum = this._keyThisFrame ? 0 : Math.min(this._pAccum - pStep, pStep);
      this._advectNow = true;
      this._frozenSalt = (Math.random() * 2147483647) | 0;
      this._writeVectorsPending = true;
    }

    this._dissolve = Math.min(DISSOLVE_MAX, this._beatsSinceKey / 4);
    this._moshUniforms.uDissolve.value = this._dissolve;
    // Per-beat figures resolved to one P-frame's share of a beat.
    const share = 1 / this._pPerBeat;
    this._moshUniforms.uDecay.value = Math.pow(this._retain, share);
    this._moshUniforms.uBias.value = BLACK_BIAS_PER_BEAT * share;
    this._moshUniforms.uFlatten.value = FLATTEN_PER_BEAT * share;
    this._moshUniforms.uChromaJit.value = CHROMA_JIT_TEXELS * Math.min(this._dissolve, 1);
    this._moshUniforms.uTime.value = this._time % 512;
  }

  _onBar() {
    this._barTookThisBeat = true;
    if (this._barsToSkip > 0) {
      // A drop: this downbeat gets no keyframe, and still counts as a beat.
      this._barsToSkip--;
      this._beatsSinceKey++;
      this._injectShape();
      return;
    }
    this._keyThisFrame = 1;      // consumed by _run, exactly one frame wide
    this._beatsSinceKey = 0;
  }

  _onBeat() {
    if (this._barTookThisBeat) return;   // the downbeat already handled this beat
    this._beatsSinceKey++;
    this._injectShape();
  }

  // The mean luminance of the freshly written buffer. Each keyframe is already
  // a reset, so this mostly idles - it exists for the drops.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buf.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1]
        + 0.114 * this._lumPixels[i + 2];
    }
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;

    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        const before = this._retain;
        this._retain = Math.max(RETAIN_MIN, this._retain - GOVERN_STEP);
        if (this._retain !== before) {
          // eslint-disable-next-line no-console
          console.log('[mosh] governor: mean luminance ' + lum.toFixed(3)
            + ' -> retain ' + before.toFixed(3) + ' -> ' + this._retain.toFixed(3));
        }
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._retain < RETAIN_MAX) {
      this._retain = Math.min(RETAIN_MAX, this._retain + GOVERN_STEP);
    }
  }

  // All the render-target work, from the display quad's onBeforeRender - the
  // only legal place a visualizer may touch the renderer.
  _run(renderer) {
    const prevTarget = renderer.getRenderTarget();
    const rebuilt = this._buf.ensureSize(renderer);
    const W = this._buf.width;
    const H = this._buf.height;

    if (rebuilt || this._lastBlockPx !== this._blockPx) {
      this._lastBlockPx = this._blockPx;
      // Block size is in SCREEN pixels; the buffer is a fraction of the screen,
      // so the count comes from the screen size and the texel size from the
      // buffer. Rounded to an EVEN count so the chroma grid at half the
      // resolution lands on luma borders rather than halfway across a block.
      const screenW = W / this._layout.scale;
      const screenH = H / this._layout.scale;
      let nx = Math.round(screenW / this._blockPx / 2) * 2;
      let ny = Math.round(screenH / this._blockPx / 2) * 2;
      nx = Math.max(4, Math.min(MV_MAX_W, nx));
      ny = Math.max(4, Math.min(MV_MAX_H, ny));
      const plan = this._gridPlan(nx, ny);
      nx = plan.nx;
      ny = plan.ny;
      this._mvW = nx;
      this._mvH = ny;
      this._moshUniforms.uBlockCount.value.set(nx, ny);
      if (this._showMat) {
        this._showMat.uniforms.uBlockScreenPx.value.set(screenW / nx, screenH / ny);
      }
      this._moshUniforms.uInvBuf.value.set(1 / W, 1 / H);
      // The DataTexture gets an exactly-sized view of the shared buffer: a view
      // longer than w*h*4 is not portable across the two GL backends here.
      this._mvTex.image = {
        data: new Uint8Array(this._mvBuffer, 0, nx * ny * 4),
        width: nx,
        height: ny
      };
      this._moshUniforms.uGridOffset.value.set(plan.ox, plan.oy);
      this._gridMarginPx = this._gridMargin * (screenW / nx);
      this._writeVectorsPending = true;
    }

    if (this._paused) { renderer.setRenderTarget(prevTarget); return; }

    if (!this._advectNow) {
      // Between P-frames the picture is simply held - which is most of the
      // display's frames, and where the saving is.
      this._showMat.uniforms.uTex.value = this._buf.read.texture;
      renderer.setRenderTarget(prevTarget);
      return;
    }
    this._advectNow = false;

    if (this._writeVectorsPending) {
      this._writeVectorsPending = false;
      this._writeVectors();
    }

    // The shapes queued on this beat, into the uniform slots for one pass only:
    // they are painted into the buffer once and thereafter belong to it.
    const su = this._moshUniforms.uShape.value;
    const sc = this._moshUniforms.uShapeCol.value;
    for (let i = 0; i < SHAPE_SLOTS; i++) {
      const s = this._pendingShapes[i];
      if (!s) { su[i].set(0, 0, 0, 0); continue; }
      su[i].set(s.x, s.y, s.w, s.h);
      _col.set(this._vivid[i % this._vivid.length]);
      _col.getHSL(_hsl);
      _col.setHSL(_hsl.h, Math.max(SHAPE_SAT_MIN, _hsl.s),
        Math.min(0.62, Math.max(0.44, _hsl.l)));
      sc[i].set(_col.r, _col.g, _col.b, s.kind);
    }
    this._pendingShapes.length = 0;

    this._moshUniforms.uKeyMix.value = this._keyThisFrame;
    this._moshUniforms.uPrevious.value = this._buf.read.texture;
    this._passQuad.material = this._moshMat;
    renderer.setRenderTarget(this._buf.write);
    renderer.render(this._passScene, this._ortho);

    this._frameCount++;
    if (this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);

    this._buf.swap();
    this._showMat.uniforms.uTex.value = this._buf.read.texture;
    renderer.setRenderTarget(prevTarget);
  }

  _loadArtwork(url) {
    loadArtworkTexture(
      url,
      { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
      (texture) => {
        const previous = this._artworkTexture;
        this._artworkTexture = texture;
        this._moshUniforms.uArtwork.value = texture;
        this._moshUniforms.uHasArtwork.value = 1;
        const img = texture.image;
        this._moshUniforms.uArtAspect.value = img && img.height ? img.width / img.height : 1;
        if (previous) previous.dispose();
      },
      () => {
        this._moshUniforms.uArtwork.value = this._blankTex;
        this._moshUniforms.uHasArtwork.value = 0;
      }
    );
  }

  dispose() {
    if (this._quad) {
      this._quad.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._quad);
      this._quad.geometry.dispose();
      this._quad = null;
    }
    if (this._passQuad) { this._passQuad.geometry.dispose(); this._passQuad = null; }
    if (this._lumQuad) {
      this._lumQuad.geometry.dispose();
      this._lumQuad.material.dispose();
      this._lumQuad = null;
    }
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    if (this._moshMat) { this._moshMat.dispose(); this._moshMat = null; }
    if (this._showMat) { this._showMat.dispose(); this._showMat = null; }
    if (this._buf) { this._buf.dispose(); this._buf = null; }
    if (this._mvTex) { this._mvTex.dispose(); this._mvTex = null; }
    if (this._blankTex) { this._blankTex.dispose(); this._blankTex = null; }
    if (this._artworkTexture) { this._artworkTexture.dispose(); this._artworkTexture = null; }
  }
}
